import axios from 'axios';
import React, { Component } from 'react'
import { navigateTo, reload } from './utils/common.util';
import moment from 'moment-timezone';

const CHUNK_RELOAD_GUARD_KEY = "stx_chunk_reload_once";

const clearChunkCaches = async () => {
    try {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }
    } catch {
        // ignore service worker cleanup errors
    }

    try {
        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
        }
    } catch {
        // ignore cache cleanup errors
    }
};

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.handleChunkReload = this.handleChunkReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    async componentDidCatch(error, errorInfo) {
        console.log("🚀 ~ ErrorBoundary ~ componentDidCatch ~ errorInfo:", errorInfo)
        console.log("🚀 ~ ErrorBoundary ~ componentDidCatch ~ error:", error)
        try {
            let errorType = 'general'

            // Detect if chunk failed to load (e.g. after deployment)
            const isChunkError = (
                error?.message?.includes('Loading chunk') ||
                error?.message?.toLowerCase()?.includes('failed to fetch dynamically imported module') ||
                error?.message?.toLowerCase()?.includes('unable to preload css for') ||
                error?.message?.toLowerCase()?.includes('importing a module script failed') ||
                error?.message?.toLowerCase()?.includes('is not a valid javascript mime type') ||
                error?.message?.toLowerCase()?.includes('error loading dynamically imported module')
            );

            if (!navigator.onLine) {
                errorType = 'network'
            } else if (isChunkError) {
                errorType = 'chunk'
                const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
                if (!hasReloaded) {
                    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, "1");
                    await clearChunkCaches();
                    window.location.reload();
                    return;
                }
            }
            this.setState({ errorType })

            if (errorType === 'chunk') return
            await axios({
                url: '/api/error/client',
                data: {
                    message: error.message,
                    stack: error.stack,
                    componentStack: errorInfo?.componentStack,

                    // route info
                    route: window.location.pathname,
                    hash: window.location.hash,
                    search: window.location.search,

                    // user info
                    // userId: this.props?.user?.userId ?? null,
                    // clientId: this.props?.user?.clientId ?? null,
                    // email: this.props?.user?.email ?? null,

                    // device info
                    userAgent: navigator.userAgent,

                    timestamp: moment().format('YYYY-MM-DD HH:mm:ss'),

                    // optional extras
                    errorType,
                },
                method: 'POST',
            });
        } catch (err) {
            console.error(err);
        }
    }

    async handleChunkReload() {
        try {
            sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
            await clearChunkCaches();
        } finally {
            reload();
        }
    }

    render() {
        if (this.state.hasError) {
            return <div className="p-5 w-100 vh-100 d-flex align-items-center justify-content-center text-wrap">
                {this.state.errorType === 'general' && <>
                    <div className='text-center'>
                        <h1 className="mb-2">⚠️</h1>
                        <p className="fs-2 fw-semibold mb-2">Oops! Something Went Wrong </p>
                        <p >Uh-oh, something went wrong, and our team of over-engineers is already knee-deep in code, tracing the issue with their trusty magnifying glasses.</p>
                        <p className="mb-2 fw-semibold">Here’s what you can do in the meantime:</p>
                        <p className="mb-1">1. Refresh the page it’s the digital equivalent of turning it off and on again.</p>
                        <p className="mb-1">2. Clear your cache and cookies because sometimes the smallest crumbs cause the biggest mess.</p>
                        <p>3. Check your connection a weak signal might be playing tricks on us.</p>
                        <div className='col-12 d-flex align-items-center justify-content-center mt-4'>
                            <button onClick={this.handleChunkReload} className='cancel-button'>Reload</button>
                            <button onClick={() => navigateTo('/')} className='btn-orange ms-2'>Home</button>
                        </div>
                        {import.meta.env.APP_ENV === 'development' && <div className='mt-3'>
                            <pre className="text-wrap text-center">
                                {this.state?.error?.stack}
                            </pre>
                        </div>}
                    </div>
                </>}
                {this.state.errorType === 'chunk' && <>
                    <div className='text-center'>
                        <h1 className="mb-2">⚠️</h1>
                        <p className='fs-2 fw-semibold mb-2'>App Updated</p>
                        <p className="text-center mb-0">You're using an older version of the app. We've made some updates in the latest release.</p>
                        <p className="text-center">Please reload to get the newest features and fixes.</p>
                        <div className='col-12 d-flex align-items-center justify-content-center mt-3'>
                            <button onClick={this.handleChunkReload} className='cancel-button'>Reload</button>
                            <button onClick={() => navigateTo('/')} className='btn-orange ms-2'>Home</button>
                        </div>
                    </div>
                </>}
                {this.state.errorType === 'network' && <>
                    <div className='text-center'>
                        <h1 className="mb-2">🌐</h1>
                        <p className='fs-2 fw-semibold mb-2'>You're Offline</p>
                        <p className='mb-0'>It looks like you’ve lost your internet connection. Please check your network and try again.</p>
                        <p>Then refresh the page to continue.</p>
                        <div className='col-12 d-flex align-items-center justify-content-center mt-3' >
                            <button onClick={() => reload()} className='btn-orange'>Retry</button>
                        </div>
                    </div>
                </>}


            </div>
                ;
        }
        return this.props.children;
    }
}

export default ErrorBoundary
